import { cn } from '@/lib/utils';

interface UserCellProps {
  /** User's database ID — used for handlers/selection, not displayed */
  id?: string;
  /** First name (or full name if lastName is omitted) */
  firstName?: string;
  lastName?: string;
  email?: string;
  /** Optional avatar URL; falls back to initials */
  avatarUrl?: string;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Additional className for the outer wrapper */
  className?: string;
  /** Override avatar background (e.g., for suspended users) */
  avatarClassName?: string;
}

const sizeMap = {
  sm: { avatar: 'h-8 w-8', initials: 'text-xs', name: 'text-sm', email: 'text-xs' },
  md: { avatar: 'h-10 w-10', initials: 'text-sm', name: 'text-sm', email: 'text-sm' },
  lg: { avatar: 'h-12 w-12', initials: 'text-base', name: 'text-base', email: 'text-sm' },
};

/**
 * Reusable user display cell — avatar (initials) + name + email.
 *
 * Pass `id` for handler use (e.g., onClick, data attributes) — it's never shown.
 *
 * ```tsx
 * <UserCell id={user.id} firstName="Jane" lastName="Doe" email="jane@co.com" />
 * ```
 */
export function UserCell({
  id,
  firstName,
  lastName,
  email,
  avatarUrl,
  size = 'md',
  className,
  avatarClassName,
}: UserCellProps) {
  const s = sizeMap[size];
  const initials = `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase() || '?';
  const fullName = [firstName, lastName].filter(Boolean).join(' ');

  return (
    <div className={cn('flex items-center gap-3', className)} data-user-id={id}>
      {/* Avatar */}
      {avatarUrl ? (
        <img src={avatarUrl} alt={fullName} className={cn('rounded-full object-cover', s.avatar)} />
      ) : (
        <div
          className={cn(
            'bg-muted flex items-center justify-center rounded-full',
            s.avatar,
            avatarClassName
          )}
        >
          <span className={cn('font-medium', s.initials)}>{initials}</span>
        </div>
      )}

      {/* Text */}
      <div className="min-w-0 flex-1">
        {fullName && <p className={cn('truncate font-medium', s.name)}>{fullName}</p>}
        {email && <p className={cn('text-muted-foreground truncate', s.email)}>{email}</p>}
      </div>
    </div>
  );
}
